import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, X, Plus, Minus, Lock, Leaf, ShieldCheck, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { API } from "../lib/api";

const CART_KEY = "ss_shop_cart";
const money = (n, c = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: c }).format(n);

const Badge = ({ children, tone = "vegan" }) => {
  const tones = {
    vegan: "bg-[#EBF2EA] text-[#2D5A27] border-[#C5DBC1]",
    gold: "bg-[#FAF4E5] text-[#8C6B13] border-[#E8D4A2]",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest ${tones[tone]}`}>
      {children}
    </span>
  );
};

const Stars = ({ rating = 4.8, reviews }) => (
  <div className="flex items-center gap-1.5" data-testid="product-rating">
    <div className="flex">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(rating) ? "fill-stylist-gold text-stylist-gold" : "text-stylist-border"}`} />
      ))}
    </div>
    {reviews != null && <span className="text-xs text-stylist-muted font-mono">{reviews}</span>}
  </div>
);

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [category, setCategory] = useState("All Products");
  const [active, setActive] = useState(null);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    fetch(`${API}/shop/products`)
      .then((r) => r.json())
      .then((d) => { setProducts(d.products || []); setConfigured(d.configured); })
      .catch(() => toast.error("Could not load the store just now."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category).filter(Boolean));
    return ["All Products", ...Array.from(set)];
  }, [products]);

  const filtered = category === "All Products" ? products : products.filter((p) => p.category === category);

  const addToCart = (product, variant) => {
    const v = variant || product.variants?.[0];
    if (!v) return;
    setCart((c) => {
      const key = v.id;
      const found = c.find((i) => i.variantId === key);
      if (found) return c.map((i) => (i.variantId === key ? { ...i, qty: i.qty + 1 } : i));
      return [...c, { variantId: key, qty: 1, title: product.title, variantTitle: v.title, price: v.price, currency: v.currency || product.currency, image: product.images?.[0] }];
    });
    toast.success(`${product.title} added to your bag`);
    setCartOpen(true);
  };

  const setQty = (variantId, delta) =>
    setCart((c) => c.map((i) => (i.variantId === variantId ? { ...i, qty: Math.max(1, i.qty + delta) } : i)));
  const removeItem = (variantId) => setCart((c) => c.filter((i) => i.variantId !== variantId));

  const count = cart.reduce((n, i) => n + i.qty, 0);
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const checkout = async () => {
    if (!cart.length) return;
    setCheckingOut(true);
    try {
      const res = await fetch(`${API}/shop/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lines: cart.map((i) => ({ variantId: i.variantId, quantity: i.qty })) }),
      });
      const data = await res.json();
      if (data.configured && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        toast.info(data.message || "Our store opens very soon. Please check back shortly.");
      }
    } catch {
      toast.error("Could not start checkout, please try again.");
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-stylist-bg" data-testid="shop-page">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-stylist-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center py-20 lg:py-28">
          <div className="lg:col-span-6">
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-stylist-gold font-semibold">The Senior Stylist Collection</span>
            <h1 className="mt-5 font-serif font-light tracking-tight text-stylist-ink leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              Bespoke Vegan Beauty <span className="italic text-stylist-gold">&</span> Botanical Haircare
            </h1>
            <p className="mt-6 max-w-md text-base sm:text-lg text-stylist-ink2 font-light leading-relaxed">
              Formulated in London with plant-based actives, designed to elevate your daily ritual. Every product is 100% vegan and cruelty-free.
            </p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              <Badge>100% Vegan</Badge>
              <Badge>Cruelty-Free</Badge>
              <Badge tone="gold">London Crafted</Badge>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-4 bg-stylist-gold/10 blur-2xl rounded-[2rem]" />
              <img
                src="https://images.unsplash.com/photo-1631730359585-38a4935cbec4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Botanical vegan haircare"
                className="relative w-full h-[360px] lg:h-[460px] object-cover rounded-[1.5rem] shadow-xl"
                data-testid="shop-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {!configured && (
        <div className="bg-stylist-ink text-stylist-bg text-center py-2.5 px-4 text-xs font-mono tracking-wide" data-testid="shop-preview-banner">
          Store preview. Our full range launches very soon.
        </div>
      )}

      {/* Catalogue */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-wrap gap-2.5 mb-10" data-testid="shop-category-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`shop-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-full px-4 py-2 text-xs font-mono uppercase tracking-widest border transition-colors ${
                category === c
                  ? "bg-stylist-ink text-stylist-bg border-stylist-ink"
                  : "bg-white/70 text-stylist-ink2 border-stylist-border hover:border-stylist-gold/50"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-stylist-gold" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8" data-testid="shop-product-grid">
            {filtered.map((p) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="group relative bg-white/80 backdrop-blur-md border border-stylist-border hover:border-stylist-gold/40 rounded-2xl overflow-hidden transition-colors"
                data-testid="shop-product-card"
              >
                <button onClick={() => setActive(p)} className="block w-full text-left" data-testid={`shop-product-open-${p.handle}`}>
                  <div className="relative overflow-hidden aspect-[4/5] bg-stylist-bg2">
                    <img src={p.images?.[0]} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                      {(p.tags || []).some((t) => /vegan/i.test(t)) && <Badge><Leaf className="w-3 h-3" /> Vegan</Badge>}
                    </div>
                  </div>
                  <div className="p-5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stylist-gold font-semibold">{p.category}</span>
                    <h3 className="mt-1.5 font-serif text-xl text-stylist-ink leading-snug">{p.title}</h3>
                    {p.rating != null && <div className="mt-2"><Stars rating={p.rating} reviews={p.reviews} /></div>}
                    <p className="mt-3 text-stylist-ink font-medium">{money(p.price, p.currency)}</p>
                  </div>
                </button>
                <div className="px-5 pb-5">
                  <button
                    onClick={() => addToCart(p)}
                    data-testid={`shop-quick-add-${p.handle}`}
                    className="w-full rounded-full bg-stylist-ink text-stylist-bg py-2.5 text-sm font-medium hover:bg-stylist-gold hover:text-stylist-ink transition-colors active:scale-95"
                  >
                    Add to bag
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <Footer />

      {/* Floating cart button */}
      <button
        onClick={() => setCartOpen(true)}
        data-testid="cart-drawer-toggle"
        className="fixed bottom-6 left-6 z-40 flex items-center gap-2 rounded-full bg-stylist-ink text-stylist-bg pl-4 pr-5 py-3 shadow-xl hover:bg-stylist-gold hover:text-stylist-ink transition-colors"
      >
        <ShoppingBag className="w-5 h-5" />
        <span className="text-sm font-medium">Bag</span>
        {count > 0 && <span className="ml-1 min-w-[20px] rounded-full bg-stylist-gold text-stylist-ink text-xs font-bold px-1.5 py-0.5 text-center" data-testid="cart-count">{count}</span>}
      </button>

      {/* Product detail modal */}
      <AnimatePresence>
        {active && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-stylist-ink/50 backdrop-blur-sm" onClick={() => setActive(null)} />
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="relative bg-stylist-bg rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto grid grid-cols-1 md:grid-cols-2"
              data-testid="shop-product-modal"
            >
              <button onClick={() => setActive(null)} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 hover:bg-white" data-testid="shop-product-modal-close"><X className="w-5 h-5 text-stylist-ink" /></button>
              <div className="bg-stylist-bg2"><img src={active.images?.[0]} alt={active.title} className="w-full h-full object-cover min-h-[280px]" /></div>
              <div className="p-8">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stylist-gold font-semibold">{active.category}</span>
                <h2 className="mt-2 font-serif text-3xl text-stylist-ink leading-tight">{active.title}</h2>
                {active.rating != null && <div className="mt-3"><Stars rating={active.rating} reviews={active.reviews} /></div>}
                <p className="mt-4 text-2xl font-serif text-stylist-ink">{money(active.price, active.currency)}</p>
                <p className="mt-4 text-sm text-stylist-ink2 leading-relaxed">{active.description}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {(active.tags || []).map((t) => <Badge key={t}>{t}</Badge>)}
                </div>
                <button
                  onClick={() => { addToCart(active); setActive(null); }}
                  data-testid="shop-modal-add-to-cart"
                  className="mt-7 w-full rounded-full bg-stylist-gold text-stylist-ink py-3 font-semibold hover:bg-stylist-ink hover:text-stylist-bg transition-colors active:scale-95"
                >
                  Add to bag
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cart drawer */}
      <AnimatePresence>
        {cartOpen && (
          <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-stylist-ink/40 backdrop-blur-sm" onClick={() => setCartOpen(false)} />
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="absolute right-0 top-0 h-full w-full max-w-[420px] bg-stylist-bg flex flex-col shadow-2xl"
              data-testid="cart-drawer"
            >
              <div className="flex items-center justify-between p-6 border-b border-stylist-border">
                <h3 className="font-serif text-2xl text-stylist-ink">Your Bag <span className="text-stylist-muted text-base">({count})</span></h3>
                <button onClick={() => setCartOpen(false)} data-testid="cart-drawer-close" className="p-2 hover:bg-stylist-bg2 rounded-full"><X className="w-5 h-5" /></button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {cart.length === 0 ? (
                  <p className="text-center text-stylist-muted py-16 font-mono text-sm">Your bag is empty.</p>
                ) : cart.map((i) => (
                  <div key={i.variantId} className="flex gap-4" data-testid="cart-item">
                    <img src={i.image} alt={i.title} className="w-16 h-20 object-cover rounded-lg bg-stylist-bg2" />
                    <div className="flex-1">
                      <p className="font-serif text-stylist-ink leading-tight">{i.title}</p>
                      <p className="text-xs text-stylist-muted font-mono mt-0.5">{i.variantTitle}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center border border-stylist-border rounded-full">
                          <button onClick={() => setQty(i.variantId, -1)} data-testid="cart-qty-dec" className="p-1.5"><Minus className="w-3.5 h-3.5" /></button>
                          <span className="text-sm w-6 text-center">{i.qty}</span>
                          <button onClick={() => setQty(i.variantId, 1)} data-testid="cart-qty-inc" className="p-1.5"><Plus className="w-3.5 h-3.5" /></button>
                        </div>
                        <button onClick={() => removeItem(i.variantId)} data-testid="cart-remove" className="text-xs text-stylist-muted hover:text-stylist-ink underline">Remove</button>
                      </div>
                    </div>
                    <p className="text-stylist-ink font-medium">{money(i.price * i.qty, i.currency)}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-stylist-border p-6 space-y-4">
                <div className="flex justify-between text-stylist-ink">
                  <span className="font-mono text-sm uppercase tracking-wide text-stylist-muted">Subtotal</span>
                  <span className="font-serif text-xl">{money(subtotal)}</span>
                </div>
                <p className="text-xs text-stylist-muted">Taxes and shipping calculated at secure checkout.</p>
                <button
                  onClick={checkout}
                  disabled={!cart.length || checkingOut}
                  data-testid="checkout-handoff-button"
                  className="w-full rounded-full bg-stylist-ink text-stylist-bg py-3.5 font-medium hover:bg-stylist-gold hover:text-stylist-ink transition-colors active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {checkingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                  {checkingOut ? "Connecting to secure checkout" : "Proceed to secure checkout"}
                </button>
                <div className="flex items-center justify-center gap-4 pt-1 text-[10px] font-mono uppercase tracking-wider text-stylist-muted">
                  <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Encrypted</span>
                  <span className="flex items-center gap-1"><Leaf className="w-3.5 h-3.5" /> 100% Vegan</span>
                </div>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
